const VARIANT_UPDATE = 'variant:update';
const QTY_UPDATE = 'quantity-selector:update';
const DURATION_OPTIONS_CHANGE = 'wd-duration-options:change';
const DURATION_OPTIONS_QTY_CHANGE = 'wd-duration-options:quantity-change';

class WdStickyAddToCart extends HTMLElement {
  #visible = false;
  #observers = [];
  #qtyInput;
  #qtyMinus;
  #qtyPlus;
  #submit;
  #submitText;
  #submitPrice;
  #priceEl;
  #imageEl;
  #addToCartLabel = 'הוספה לסל';

  connectedCallback() {
    this.#resolveSectionId();

    this.#qtyInput = this.querySelector('[data-sticky-qty-input]');
    this.#qtyMinus = this.querySelector('[data-sticky-qty-minus]');
    this.#qtyPlus = this.querySelector('[data-sticky-qty-plus]');
    this.#submit = this.querySelector('[data-sticky-submit]');
    this.#submitText = this.querySelector('[data-sticky-submit-text]');
    this.#submitPrice = this.querySelector('[data-sticky-submit-price]');
    this.#priceEl = this.querySelector('[data-sticky-price]');
    this.#imageEl = this.querySelector('[data-sticky-image]');

    this.#bindQuantity();
    this.#syncQuantityVisibility();
    this.#submit?.addEventListener('click', this.#onSubmit);
    document.addEventListener(VARIANT_UPDATE, this.#onVariantUpdate);
    document.addEventListener('variantChangeEnd', this.#onLegacyVariantChange);
    document.addEventListener(DURATION_OPTIONS_CHANGE, this.#onDurationOptionsChange);
    document.addEventListener(DURATION_OPTIONS_QTY_CHANGE, this.#onDurationOptionsQuantityChange);
    document.addEventListener(QTY_UPDATE, this.#onMainQuantityUpdate);
    document.addEventListener('change', this.#onVariantIdChange, true);

    this.#watchVariantInput();
    this.#setupVisibility();
    this.#syncFromMain({ price: true, quantity: true, availability: true });
    this.#syncFromDurationOptions();
    this.#syncSubmitPrice();
  }

  disconnectedCallback() {
    this.#observers.forEach((o) => o.disconnect());
    this.#observers = [];
    document.removeEventListener(VARIANT_UPDATE, this.#onVariantUpdate);
    document.removeEventListener('variantChangeEnd', this.#onLegacyVariantChange);
    document.removeEventListener(DURATION_OPTIONS_CHANGE, this.#onDurationOptionsChange);
    document.removeEventListener(DURATION_OPTIONS_QTY_CHANGE, this.#onDurationOptionsQuantityChange);
    document.removeEventListener(QTY_UPDATE, this.#onMainQuantityUpdate);
    document.removeEventListener('change', this.#onVariantIdChange, true);
    this.#submit?.removeEventListener('click', this.#onSubmit);
    document.body.classList.remove('wd-sticky-atc-active');
  }

  #resolveSectionId() {
    if (this.dataset.sectionId && this.dataset.sectionId !== 'global') return;

    const anchor =
      document.querySelector('.wt-product__add-to-cart') ||
      document.querySelector('wd-duration-options') ||
      document.querySelector('.product-information') ||
      document.querySelector('.buy-buttons-block');

    const section = anchor?.closest('.shopify-section, section[id^="shopify-section-"]');
    const sectionId = section?.id?.replace(/^shopify-section-/, '');
    if (sectionId) this.dataset.sectionId = sectionId;
  }

  #syncQuantityVisibility() {
    const hideForSingleVariant = this.hasAttribute('data-single-variant');
    const hideForPackageOptions = Boolean(
      document.querySelector('wd-duration-options'),
    );
    this.classList.toggle(
      'wd-sticky-atc--no-quantity',
      hideForSingleVariant || hideForPackageOptions,
    );
  }

  #hasPackageOptions() {
    return Boolean(document.querySelector('wd-duration-options'));
  }

  #getPackageCount(visualQuantity) {
    if (this.#hasPackageOptions()) return 1;

    return Math.max(
      1,
      visualQuantity ||
        parseInt(this.#getMainQuantityInput()?.value, 10) ||
        parseInt(this.#qtyInput?.value, 10) ||
        1,
    );
  }

  #bindQuantity() {
    this.#qtyMinus?.addEventListener('click', () => {
      if (!this.#qtyInput) return;
      this.#qtyInput.stepDown();
      this.#setQuantity(this.#qtyInput.value);
    });

    this.#qtyPlus?.addEventListener('click', () => {
      if (!this.#qtyInput) return;
      this.#qtyInput.stepUp();
      this.#setQuantity(this.#qtyInput.value);
    });

    this.#qtyInput?.addEventListener('change', () => {
      this.#setQuantity(this.#qtyInput.value);
    });
  }

  #setQuantity(value) {
    const qty = Math.max(1, parseInt(value, 10) || 1);
    if (this.#qtyInput) this.#qtyInput.value = String(qty);

    const mainQty = this.#getMainQuantityInput();
    if (mainQty && mainQty.value !== String(qty)) {
      mainQty.value = String(qty);
      mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      mainQty.dispatchEvent(new Event('input', { bubbles: true }));
    }

    document.querySelector('wd-duration-options')?.syncBeforeSubmit?.();

    const selectedRadio = document.querySelector('.wd-pdo__input:checked');
    if (selectedRadio) {
      const variants = this.#readDurationVariants();
      const variant = variants.find(
        (item) => String(item.id) === String(selectedRadio.dataset.variantId),
      );
      if (variant) {
        this.#updatePriceFromVariant(
          variant,
          parseInt(selectedRadio.dataset.packUnits, 10) || 1,
          parseInt(selectedRadio.dataset.linePriceCents, 10) || 0,
          qty,
        );
      }
    }
  }

  #onMainQuantityUpdate = (event) => {
    if (!this.#qtyInput || event.target?.closest?.('wd-sticky-add-to-cart')) return;

    const mainQty = this.#getMainQuantityInput();
    if (!mainQty) return;

    const qty = parseInt(mainQty.value, 10) || 1;
    if (String(qty) !== this.#qtyInput.value) {
      this.#qtyInput.value = String(qty);
    }
  };

  #onVariantIdChange = (event) => {
    if (event.target?.name !== 'id') return;
    if (event.target.closest('wd-sticky-add-to-cart')) return;

    const form = this.#getMainForm();
    if (form && !form.contains(event.target)) return;

    requestAnimationFrame(() => {
      this.#syncPriceFromMain();
      this.#syncImageFromMain();
      this.#syncFromMain({ availability: true });
    });
  };

  #onSubmit = (event) => {
    event.preventDefault();

    const mainQty = this.#getMainQuantityInput();
    if (mainQty && this.#qtyInput) {
      mainQty.value = this.#qtyInput.value;
      mainQty.dispatchEvent(new Event('change', { bubbles: true }));
    }

    document.querySelector('wd-duration-options')?.syncBeforeSubmit?.();

    const submitButton = this.#getMainSubmitButton();
    if (submitButton && !submitButton.disabled) {
      submitButton.click();
      return;
    }

    const form = this.#getMainForm();
    if (form) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    }
  };

  #onVariantUpdate = (event) => {
    const productId = this.dataset.productId;
    const eventProductId = event.detail?.data?.productId;

    if (eventProductId && productId && eventProductId !== productId) return;

    const variant = event.detail?.resource;
    if (variant) {
      this.#updateFromVariant(variant);
    }

    this.#syncPriceFromMain(event.detail?.data?.html);
    this.#syncFromMain({ availability: true });
  };

  #onLegacyVariantChange = () => {
    this.#syncPriceFromMain();
    this.#syncImageFromMain();
    this.#syncFromMain({ availability: true });
  };

  #onDurationOptionsChange = (event) => {
    this.#syncQuantityVisibility();
    this.#syncVisualQuantityFromMain();

    const variant = event.detail?.variant;
    const packUnits = event.detail?.packUnits;
    const linePriceCents = event.detail?.linePriceCents;
    const visualQuantity = event.detail?.visualQuantity;
    if (variant) {
      this.#updateFromVariant(variant);
      this.#updatePriceFromVariant(
        variant,
        packUnits,
        linePriceCents,
        visualQuantity,
      );
    } else {
      this.#syncFromDurationOptions();
    }
    this.#syncFromMain({ availability: true });
  };

  #onDurationOptionsQuantityChange = (event) => {
    const visualQuantity = event.detail?.visualQuantity;
    if (this.#qtyInput && visualQuantity) {
      this.#qtyInput.value = String(visualQuantity);
    }

    const selectedRadio = document.querySelector('.wd-pdo__input:checked');
    const variants = this.#readDurationVariants();
    const variant = variants.find(
      (item) => String(item.id) === String(selectedRadio?.dataset?.variantId),
    );

    if (variant) {
      this.#updatePriceFromVariant(
        variant,
        event.detail?.packUnits,
        event.detail?.linePriceCents,
        visualQuantity,
      );
    }
  };

  #syncVisualQuantityFromMain() {
    if (this.#hasPackageOptions()) {
      if (this.#qtyInput) this.#qtyInput.value = '1';
      return;
    }

    const mainQty = this.#getMainQuantityInput();
    if (!mainQty || !this.#qtyInput) return;

    const qty = Math.max(1, parseInt(mainQty.value, 10) || 1);
    this.#qtyInput.value = String(qty);
  }

  #readDurationVariants() {
    try {
      const json = document.querySelector('.wd-pdo__variants-json');
      return json?.textContent ? JSON.parse(json.textContent) : [];
    } catch {
      return [];
    }
  }

  #syncFromDurationOptions() {
    const durationOptions = document.querySelector('wd-duration-options');
    if (!durationOptions) return;

    const selectedRadio = durationOptions.querySelector('.wd-pdo__input:checked');
    const variantId = selectedRadio?.dataset?.variantId;
    if (!variantId) return;

    let variants = [];
    try {
      const json = durationOptions.querySelector('.wd-pdo__variants-json');
      variants = json?.textContent ? JSON.parse(json.textContent) : [];
    } catch {
      variants = [];
    }

    const variant = variants.find((item) => String(item.id) === String(variantId));
    if (variant) {
      const packUnits = Math.max(
        1,
        parseInt(selectedRadio?.dataset?.packUnits, 10) || 1,
      );
      const linePriceCents = parseInt(selectedRadio?.dataset?.linePriceCents, 10) || 0;
      this.#updateFromVariant(variant);
      this.#updatePriceFromVariant(
        variant,
        packUnits,
        linePriceCents,
        1,
      );
    }
  }

  #updateFromVariant(variant) {
    const imageSrc =
      variant.featured_media?.preview_image?.src ||
      variant.featured_image?.src ||
      variant.featured_image?.url;

    if (imageSrc && this.#imageEl) {
      this.#imageEl.src = imageSrc;
    }

    if (typeof variant.available === 'boolean') {
      this.#setAvailable(variant.available);
    }
  }

  #formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.theme?.moneyFormat || Theme?.moneyFormat || '{{amount}}',
      );
    }
    return `₪${(cents / 100).toFixed(2)}`;
  }

  #updatePriceFromVariant(variant, packUnits, linePriceCents, visualQuantity) {
    const stickyPrice = this.#priceEl?.querySelector('.price');
    if (!stickyPrice || variant?.price == null) return;

    const selectedRadio = document.querySelector('.wd-pdo__input:checked');
    const fromRadio = parseInt(selectedRadio?.dataset?.linePriceCents, 10) || 0;
    const hasPackageOptions = this.#hasPackageOptions();
    const units = Math.max(
      1,
      packUnits ||
        parseInt(selectedRadio?.dataset?.packUnits, 10) ||
        1,
    );
    const packages = this.#getPackageCount(visualQuantity);
    const perPackagePrice = hasPackageOptions
      ? linePriceCents > 0
        ? linePriceCents
        : fromRadio > 0
          ? fromRadio
          : variant.price * units
      : linePriceCents > 0
        ? linePriceCents
        : fromRadio > 0
          ? fromRadio
          : variant.price * units;
    const linePrice = perPackagePrice * packages;
    const lineCompareCents = parseInt(selectedRadio?.dataset?.lineCompareCents, 10) || 0;
    const perPackageCompare =
      lineCompareCents > 0
        ? lineCompareCents
        : variant.compare_at_price > variant.price
          ? variant.compare_at_price * units
          : 0;
    const lineCompare = perPackageCompare > 0 ? perPackageCompare * packages : 0;

    const price = this.#formatMoney(linePrice);
    const onSale = lineCompare > linePrice;

    if (onSale) {
      const compare = this.#formatMoney(lineCompare);
      stickyPrice.className = 'price price--on-sale';
      stickyPrice.innerHTML = `
        <div class="price__container">
          <div class="price__sale visible-main-product">
            <div class="price__sale__details-wrapper">
              <span class="price-item price-item--sale price-item--last wt-product__price__final">${price}</span>
            </div>
            <span>
              <s class="price-item price-item--regular wt-product__price__compare">${compare}</s>
            </span>
          </div>
        </div>
      `;
    } else {
      stickyPrice.className = 'price';
      stickyPrice.innerHTML = `
        <div class="price__container">
          <div class="price__regular">
            <span class="price-item price-item--regular wt-product__price__final">${price}</span>
          </div>
        </div>
      `;
    }

    this.#syncSubmitPrice(price);
  }

  #syncSubmitPrice(explicitPrice) {
    if (!this.#submitPrice) return;

    if (explicitPrice) {
      this.#submitPrice.textContent = explicitPrice;
      return;
    }

    const stickyPrice = this.#priceEl?.querySelector('.price');
    if (!stickyPrice) return;

    const salePrice = stickyPrice.querySelector(
      '.price-item--sale.wt-product__price__final, .price__sale .wt-product__price__final',
    );
    const regularPrice = stickyPrice.querySelector(
      '.price__regular .wt-product__price__final, .price-item--regular.wt-product__price__final',
    );
    const displayPrice = salePrice || regularPrice;

    if (displayPrice?.textContent?.trim()) {
      this.#submitPrice.textContent = displayPrice.textContent.trim();
    }
  }

  #syncPriceFromMain(htmlDocument) {
    const doc = htmlDocument || document;
    const mainPrice =
      doc.querySelector?.('.product-details product-price .price') ||
      doc.querySelector?.('product-price .price') ||
      doc.querySelector?.('.wt-product__price .price') ||
      doc.querySelector?.('#price-' + this.dataset.sectionId + ' .price');

    const stickyPrice = this.#priceEl?.querySelector('.price');
    if (mainPrice && stickyPrice && mainPrice.innerHTML.trim()) {
      stickyPrice.className = mainPrice.className;
      if (mainPrice.innerHTML !== stickyPrice.innerHTML) {
        stickyPrice.innerHTML = mainPrice.innerHTML;
      }
      this.#syncSubmitPrice();
      return;
    }

    this.#syncFromDurationOptions();
    this.#syncSubmitPrice();
  }

  #syncImageFromMain() {
    const selectedCard = document.querySelector('.wd-pdo__item.is-selected img');
    const mainImage =
      selectedCard ||
      document.querySelector('.product-information__media img') ||
      document.querySelector('.wt-product__gallery img:not([hidden])') ||
      document.querySelector('.wt-product__gallery img');

    if (mainImage?.src && this.#imageEl) {
      this.#imageEl.src = mainImage.currentSrc || mainImage.src;
    }
  }

  #syncFromMain({ price = false, quantity = false, availability = false } = {}) {
    if (price) this.#syncPriceFromMain();
    if (quantity) {
      const mainQty = this.#getMainQuantityInput();
      if (mainQty && this.#qtyInput) {
        this.#qtyInput.value = mainQty.value || '1';
      }
    }
    if (availability) {
      const submit = this.#getMainSubmitButton();
      if (submit) {
        this.#setAvailable(!submit.disabled && submit.getAttribute('aria-disabled') !== 'true');
      }
    }
  }

  #setAvailable(available) {
    if (!this.#submit) return;
    this.#submit.disabled = !available;
    if (this.#submitText) {
      this.#submitText.textContent = available ? this.#addToCartLabel : 'אזל מהמלאי';
    }

    if (this.#submitPrice) {
      this.#submitPrice.hidden = !available;
    }
  }

  #watchVariantInput() {
    const variantInput = this.#getVariantInput();
    if (!variantInput) return;

    const observer = new MutationObserver(() => {
      this.#syncPriceFromMain();
      this.#syncImageFromMain();
      this.#syncFromMain({ availability: true });
    });

    observer.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    this.#observers.push(observer);
  }

  #setupVisibility() {
    const targets = [
      document.querySelector('.buy-buttons-block'),
      document.querySelector('.product-form-buttons'),
      document.querySelector('.wt-product__add-to-cart'),
      document.querySelector('.main-product__buy-buttons--container'),
      document.querySelector('.wt-product__add-to-cart_form--wrapper'),
    ].filter(Boolean);

    if (!targets.length) return;

    const footer = document.querySelector('footer, .wt-footer');
    const visibleTargets = new Set();

    const updateVisibility = () => {
      const shouldShow = visibleTargets.size === 0;
      this.#toggleVisible(shouldShow);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            visibleTargets.add(target);
          } else {
            visibleTargets.delete(target);
          }
        });
        updateVisibility();
      },
      { threshold: 0, rootMargin: '0px 0px 0px 0px' },
    );

    targets.forEach((el) => observer.observe(el));
    if (footer) observer.observe(footer);

    this.#observers.push(observer);
  }

  #toggleVisible(show) {
    if (show === this.#visible) return;
    this.#visible = show;
    this.classList.toggle('is-visible', show);
    document.body.classList.toggle('wd-sticky-atc-active', show);
  }

  #getMainForm() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;

    if (sectionId && sectionId !== 'global') {
      const sectionForm = document.getElementById(`product-form-${sectionId}`);
      if (sectionForm) return sectionForm;
    }

    return (
      document.querySelector(`product-form-component[data-product-id="${productId}"] form`) ||
      document.querySelector('product-form-component form[data-type="add-to-cart-form"]') ||
      document.querySelector('.wt-product__add-to-cart product-form form') ||
      document.querySelector('.wt-product__add-to-cart_form') ||
      document.querySelector('product-form form')
    );
  }

  #getVariantInput() {
    const form = this.#getMainForm();
    return (
      form?.querySelector('[ref="variantId"]') ||
      form?.querySelector('input[name="id"]') ||
      document.querySelector('.wt-product input[name="id"]')
    );
  }

  #getMainQuantityInput() {
    const form = this.#getMainForm();
    const visualInForm = form?.querySelector('.wd-pdo-visual-quantity');
    if (visualInForm) return visualInForm;

    const quantitySelector =
      'input[name="quantity"]:not(.wd-pdo-cart-quantity), .js-counter-quantity';

    const inForm =
      form?.querySelector(quantitySelector);
    if (inForm) return inForm;

    return (
      document.querySelector('.wd-pdo-visual-quantity') ||
      document.querySelector('.buy-buttons-block input[name="quantity"]:not(.wd-pdo-cart-quantity)') ||
      document.querySelector('.product-form-buttons input[name="quantity"]:not(.wd-pdo-cart-quantity)') ||
      document.querySelector('.wt-product__add-to-cart input[name="quantity"]:not(.wd-pdo-cart-quantity)') ||
      document.querySelector('.js-counter-quantity')
    );
  }

  #getMainSubmitButton() {
    const form = this.#getMainForm();
    return (
      form?.querySelector('.add-to-cart-button button[type="submit"]') ||
      form?.querySelector('button[type="submit"][name="add"]') ||
      form?.querySelector('.js-add-to-cart') ||
      document.querySelector('.wt-product__add-to-cart .product-form__submit') ||
      document.querySelector('.js-add-to-cart')
    );
  }
}

if (!customElements.get('wd-sticky-add-to-cart')) {
  customElements.define('wd-sticky-add-to-cart', WdStickyAddToCart);
}
